import axios from 'axios';
import { clearAuthSession, loadAuthSession, saveAuthSession, verifyToken } from 'contexts/auth/session';

export const GESTOP_API_BASE_URL = process.env.REACT_APP_API;
export const GESTOP_CONNECTION_NAME = process.env.REACT_APP_CONN;

const LOGIN_PATH = `${(process.env.REACT_APP_BASEPATH || '').replace(/\/+$/, '')}/login`;
const REFRESH_TOKEN_ENDPOINT = 'security/refreshtoken';
const LOGOUT_ENDPOINT = 'security/logout';
const AUTH_ENDPOINTS_WITHOUT_RECOVERY = ['security/login', REFRESH_TOKEN_ENDPOINT, LOGOUT_ENDPOINT];

const gestopAPI = axios.create({
    baseURL: GESTOP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-ConnName': GESTOP_CONNECTION_NAME
    }
});

const gestopApiForExternalEndpoints = axios.create({
    baseURL: GESTOP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-ConnName': GESTOP_CONNECTION_NAME
    }
});

// Dedicated client for the refresh call: it must never carry the Authorization header nor run the auth interceptors.
const authRefreshClient = axios.create({
    baseURL: GESTOP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-ConnName': GESTOP_CONNECTION_NAME
    }
});

// ==============================|| AUTH SESSION RECOVERY ||============================== //

/**
 * Error raised when the stored session cannot be recovered (no refresh token, or the refresh token was rejected).
 * Transient failures (network errors, server errors) are propagated as the original axios error instead.
 */
export class AuthSessionExpiredError extends Error {
    constructor(message = 'The authentication session has expired.', cause = null) {
        super(message);
        this.name = 'AuthSessionExpiredError';
        this.isAuthSessionExpired = true;
        this.cause = cause;
    }
}

let refreshPromise = null;
let sessionExpiryHandled = false;
const authSessionHandlers = { onRefreshed: null, onExpired: null };

/**
 * Registers callbacks used to keep the React authentication state in sync with the session recovery performed here.
 * @param {{ onRefreshed?: Function, onExpired?: Function }} handlers
 * @returns {Function} unregister callback
 */
export const registerAuthSessionHandlers = ({ onRefreshed = null, onExpired = null } = {}) => {
    authSessionHandlers.onRefreshed = onRefreshed;
    authSessionHandlers.onExpired = onExpired;

    return () => {
        if (authSessionHandlers.onRefreshed === onRefreshed) authSessionHandlers.onRefreshed = null;
        if (authSessionHandlers.onExpired === onExpired) authSessionHandlers.onExpired = null;
    };
};

/**
 * Re-enables the session expiry handling after a new session has been established (e.g. a successful login).
 */
export const resetAuthSessionExpiry = () => {
    sessionExpiryHandled = false;
};

const isTokenUsable = (token) => {
    try {
        return verifyToken(token);
    } catch (error) {
        return false;
    }
};

const getRequestPath = (config = {}) => (config?.url || '').replace(/^\/+/, '').split('?')[0];

const isAuthEndpoint = (config = {}) => {
    const requestPath = getRequestPath(config);

    return AUTH_ENDPOINTS_WITHOUT_RECOVERY.some((endpoint) => requestPath === endpoint || requestPath.endsWith(`/${endpoint}`));
};

const isLogoutEndpoint = (config = {}) => {
    const requestPath = getRequestPath(config);

    return requestPath === LOGOUT_ENDPOINT || requestPath.endsWith(`/${LOGOUT_ENDPOINT}`);
};

const hasEntries = (value) => Boolean(value && typeof value === 'object' && Object.keys(value).length > 0);

const setAuthorizationHeader = (config, token) => {
    // eslint-disable-next-line no-param-reassign
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };

    return config;
};

const redirectToLogin = () => {
    if (typeof window === 'undefined') return;

    if (window.location.pathname !== LOGIN_PATH) {
        window.location.assign(LOGIN_PATH);
    }
};

/**
 * Clears the local session and redirects to the login page exactly once, regardless of how many requests fail in parallel.
 */
export const handleAuthSessionExpired = () => {
    if (sessionExpiryHandled) return;
    sessionExpiryHandled = true;

    clearAuthSession();

    try {
        if (typeof authSessionHandlers.onExpired === 'function') {
            authSessionHandlers.onExpired();
        }
    } catch (error) {
        // The local session is already cleared; a failing UI callback must not prevent the redirect.
    }

    redirectToLogin();
};

const requestNewSession = async () => {
    const storedSession = loadAuthSession();
    const refreshToken = storedSession?.refreshToken;

    if (!refreshToken) {
        throw new AuthSessionExpiredError('No refresh token is available.');
    }

    let response = null;

    try {
        response = await authRefreshClient.post(REFRESH_TOKEN_ENDPOINT, { refresh_token: refreshToken });
    } catch (error) {
        const status = error?.response?.status;

        // Only a client error from the refresh endpoint means that the refresh token is invalid or revoked.
        // Network errors (no response) and server errors are transient: keep the session so it can recover later.
        if (status >= 400 && status < 500) {
            throw new AuthSessionExpiredError('The refresh token was rejected.', error);
        }

        throw error;
    }

    const { token, refresh_token: nextRefreshToken, user, profileSettings, license } = response?.data || {};

    if (!token) {
        throw new AuthSessionExpiredError('The refresh response did not include a token.');
    }

    // The session may have been cleared (logout) or replaced (new login) while the refresh was in flight.
    const currentSession = loadAuthSession();

    if (!currentSession || currentSession.refreshToken !== refreshToken) {
        if (currentSession?.serviceToken && isTokenUsable(currentSession.serviceToken)) {
            return currentSession;
        }

        throw new AuthSessionExpiredError('The session changed while the token was being refreshed.');
    }

    const nextSession = saveAuthSession({
        ...currentSession,
        serviceToken: token,
        refreshToken: nextRefreshToken || refreshToken,
        user: hasEntries(user) ? user : currentSession.user,
        profileSettings: Array.isArray(profileSettings) ? profileSettings : currentSession.profileSettings,
        license: hasEntries(license) ? license : currentSession.license
    });

    try {
        if (typeof authSessionHandlers.onRefreshed === 'function') {
            authSessionHandlers.onRefreshed(nextSession);
        }
    } catch (error) {
        // The new session is already persisted; a failing UI callback must not fail the refresh.
    }

    return nextSession;
};

/**
 * Refreshes the authentication session with the stored refresh token.
 * Concurrent callers share a single in-flight refresh request.
 * @returns {Promise<object>} the persisted session
 */
export const refreshAuthSession = () => {
    if (!refreshPromise) {
        refreshPromise = requestNewSession().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

/**
 * Refreshes the session for an outgoing or failed request. Expires the session when it cannot be recovered;
 * transient failures are rethrown without touching the stored session.
 */
const refreshForRequest = async () => {
    try {
        return await refreshAuthSession();
    } catch (error) {
        if (error?.isAuthSessionExpired) {
            handleAuthSessionExpired();
        }

        throw error;
    }
};

const authRequestInterceptor = async (config) => {
    const token = localStorage.getItem('serviceToken');

    // Logout must be sent with the current token as is, even when it is expired or already rejected.
    if (isLogoutEndpoint(config)) {
        return token ? setAuthorizationHeader(config, token) : config;
    }

    if (token && isTokenUsable(token)) {
        return setAuthorizationHeader(config, token);
    }

    // Without any stored session the request is sent as is; the server decides and the response interceptor recovers.
    if (!token && !localStorage.getItem('refreshToken')) {
        return config;
    }

    // The token is missing or expired by the local clock: refresh it before sending the request.
    // A failed refresh rejects the request instead of sending it without a token.
    const session = await refreshForRequest();

    // The token was refreshed just now; a 401 on this request must not trigger a second refresh.
    // eslint-disable-next-line no-underscore-dangle
    config._retry = true;

    return setAuthorizationHeader(config, session.serviceToken);
};

const createAuthResponseErrorInterceptor = (instance) => async (error) => {
    const originalConfig = error?.config;

    if (
        error?.response?.status !== 401 ||
        !originalConfig ||
        // eslint-disable-next-line no-underscore-dangle
        originalConfig._retry ||
        isAuthEndpoint(originalConfig)
    ) {
        return Promise.reject(error);
    }

    // eslint-disable-next-line no-underscore-dangle
    originalConfig._retry = true;

    const sentAuthorization = originalConfig.headers?.Authorization;
    const currentToken = localStorage.getItem('serviceToken');

    // Another request has already refreshed the session after this one was sent: retry with the current token.
    if (currentToken && sentAuthorization !== `Bearer ${currentToken}` && isTokenUsable(currentToken)) {
        return instance(setAuthorizationHeader(originalConfig, currentToken));
    }

    let session = null;

    try {
        session = await refreshForRequest();
    } catch (refreshError) {
        return Promise.reject(refreshError?.isAuthSessionExpired ? error : refreshError);
    }

    return instance(setAuthorizationHeader(originalConfig, session.serviceToken));
};

const applyAuthInterceptors = (instance) => {
    instance.interceptors.request.use(authRequestInterceptor);
    instance.interceptors.response.use((response) => response, createAuthResponseErrorInterceptor(instance));

    return instance;
};

applyAuthInterceptors(gestopAPI);

const gestopApiWithoutHeader = (headers) => {
    const HEADERS = headers || {};

    return applyAuthInterceptors(
        axios.create({
            baseURL: GESTOP_API_BASE_URL,
            headers: {
                'X-ConnName': GESTOP_CONNECTION_NAME,
                ...HEADERS
            },
            withCredentials: true
        })
    );
};

export { gestopApiForExternalEndpoints, gestopApiWithoutHeader };
export default gestopAPI;
